import * as React from "react";
import { Avatar as AvatarPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";

function Avatar({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      className={cn("relative flex size-9 shrink-0 overflow-hidden rounded-full", className)}
      {...props}
    />
  );
}

function djb2(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function avatarTone(initials: string): string {
  const idx = (djb2(initials.toUpperCase()) % 8) + 1;
  return `var(--avatar-${idx})`;
}

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return (first + last).toUpperCase();
}

function AvatarFallback({
  className,
  initials,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> & { initials: string }) {
  return (
    <AvatarPrimitive.Fallback
      style={{ backgroundColor: avatarTone(initials) }}
      className={cn(
        "flex size-full items-center justify-center text-sm font-semibold text-white tracking-[.02em]",
        className,
      )}
      {...props}
    >
      {initials}
    </AvatarPrimitive.Fallback>
  );
}

function NameAvatar({ name, className }: { name: string; className?: string }) {
  return (
    <Avatar className={className}>
      <AvatarFallback initials={initialsFromName(name)} />
    </Avatar>
  );
}

function UserIdentityCell({ name, email }: { name: string; email: string }) {
  return (
    <div className="flex items-center gap-2">
      <NameAvatar name={name} />
      <div className="flex flex-col leading-tight">
        <span className="text-[13px] font-semibold text-ink-900">{name}</span>
        <span className="text-[12px] text-ink-500">{email}</span>
      </div>
    </div>
  );
}

export { Avatar, AvatarFallback, NameAvatar, UserIdentityCell };
